import { useEffect, useState, useCallback } from 'react';
import { Shield, X, ChevronLeft, ChevronRight, Plus, Minus, Check, ShoppingCart, AlertCircle, CreditCard, Lock, Star, Zap, Tag } from 'lucide-react';
import { useDealerAuth } from '../DealerAuthContext';
import { useDealerCart } from '../DealerCartContext';
import { dealerCreateOrder } from '../../services/api';

const BASE = process.env.REACT_APP_BACKEND_URL;

// For local /api/uploads/ images — use relative path (works from any domain via CNAME)
// For external http:// images — use full URL
function imgUrl(path) {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return path; // relative path works from same server regardless of domain
}

const FINISH_COLORS = {
  SS:  { label: 'Smooth Stainless',        swatch: '#b8b8b8', text: '#222' },
  GRF: { label: 'Graphite Black Cerakote', swatch: '#2a2a2a', text: '#fff' },
  FDE: { label: 'FDE – Flat Dark Earth',   swatch: '#9E8A5A', text: '#fff' },
};

// ── Image Gallery ─────────────────────────────────────────────────────────────
function ImageGallery({ images, name }) {
  const [idx, setIdx] = useState(0);
  const prev = () => setIdx(i => (i - 1 + images.length) % images.length);
  const next = () => setIdx(i => (i + 1) % images.length);

  return (
    <div className="space-y-3">
      {/* Main image */}
      <div className="relative bg-[#0f0f0f] aspect-square overflow-hidden">
        <img
          src={imgUrl(images[idx])}
          alt={name}
          className="w-full h-full object-contain p-4"
          onError={e => { e.target.src = ''; e.target.style.display = 'none'; }}
        />
        {/* Arrows always visible on mobile, hover-only on desktop */}
        {images.length > 1 && (
          <>
            <button onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/70 text-white p-2 active:bg-black transition-colors touch-manipulation"
              aria-label="Previous image">
              <ChevronLeft size={18} />
            </button>
            <button onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/70 text-white p-2 active:bg-black transition-colors touch-manipulation"
              aria-label="Next image">
              <ChevronRight size={18} />
            </button>
            {/* Dot indicators */}
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
              {images.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)}
                  className={`w-1.5 h-1.5 rounded-full transition-all touch-manipulation ${i === idx ? 'bg-orange-500 w-3' : 'bg-white/40'}`} />
              ))}
            </div>
          </>
        )}
      </div>
      {/* Thumbnails — hidden on very small screens */}
      {images.length > 1 && (
        <div className="hidden sm:flex gap-2 flex-wrap">
          {images.map((img, i) => (
            <button key={i} onClick={() => setIdx(i)}
              className={`w-14 h-14 bg-[#0f0f0f] overflow-hidden flex-shrink-0 border-2 transition-all touch-manipulation ${i === idx ? 'border-orange-500' : 'border-transparent hover:border-white/30'}`}>
              <img src={imgUrl(img)} alt="" className="w-full h-full object-contain p-1"
                onError={e => { e.target.style.display = 'none'; }} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Product Detail Modal ───────────────────────────────────────────────────────
function ProductModal({ product, onClose, onAddToCart }) {
  const [finish, setFinish] = useState('SS');
  const [qty, setQty] = useState(1);
  const images = product.images?.length ? product.images : [product.image].filter(Boolean);

  const handleAdd = () => {
    onAddToCart(product, qty, finish);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-50 flex items-end sm:items-center justify-center sm:p-3 overflow-y-auto">
      <div className="bg-[#111] border-t sm:border border-white/10 w-full sm:max-w-3xl sm:my-4 max-h-[95vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 sticky top-0 bg-[#111] z-10">
          <div className="flex items-center gap-2">
            <Shield size={14} className="text-orange-500" />
            <span className="font-inter text-xs text-orange-400 uppercase tracking-widest">NFA Suppressor</span>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2 touch-manipulation"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* Left: images — compact on mobile */}
          <div className="p-4 sm:p-5 border-b md:border-b-0 md:border-r border-white/10">
            <ImageGallery images={images} name={product.name} />
          </div>

          {/* Right: detail */}
          <div className="p-4 sm:p-5 flex flex-col">
            <h2 className="font-bebas text-2xl sm:text-3xl text-white tracking-wider leading-tight mb-1">{product.name}</h2>
            {product.subtitle && <p className="font-inter text-xs text-zinc-500 mb-3">{product.subtitle}</p>}

            {/* Pricing */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="font-bebas text-3xl text-orange-400 tracking-wide">${product.effective_dealer_price?.toFixed(2)}</span>
              <span className="font-inter text-sm text-zinc-600 line-through">${product.retail_price?.toFixed(2)}</span>
              <span className="font-inter text-xs text-green-400 border border-green-500/30 bg-green-500/10 px-2 py-0.5">
                SAVE {product.dealer_discount_pct}%
              </span>
            </div>

            {/* Short description */}
            <p className="font-inter text-sm text-zinc-400 leading-relaxed mb-4">{product.short_desc || product.description?.slice(0, 160)}</p>

            {/* Finish selector */}
            {product.finish_options?.length > 0 && (
              <div className="mb-4">
                <p className="font-inter text-xs text-zinc-500 uppercase tracking-widest mb-2">
                  Finish — <span className="text-white">{FINISH_COLORS[finish]?.label || finish}</span>
                </p>
                <div className="flex gap-3">
                  {product.finish_options.map(f => (
                    <button key={f.key} onClick={() => setFinish(f.key)}
                      title={f.label}
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-all touch-manipulation ${
                        finish === f.key ? 'border-orange-500 scale-110' : 'border-zinc-600'
                      }`}
                      style={{ background: FINISH_COLORS[f.key]?.swatch || '#888' }}>
                      {finish === f.key && <Check size={14} style={{ color: FINISH_COLORS[f.key]?.text || '#fff' }} />}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity — large touch targets */}
            <div className="flex items-center gap-3 mb-4">
              <p className="font-inter text-xs text-zinc-500 uppercase tracking-widest">Quantity</p>
              <div className="flex items-center border border-white/20">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors touch-manipulation"><Minus size={16} /></button>
                <span className="px-5 py-3 font-bebas text-xl text-white min-w-[44px] text-center">{qty}</span>
                <button onClick={() => setQty(q => q + 1)} className="px-4 py-3 text-zinc-400 hover:text-white hover:bg-white/5 transition-colors touch-manipulation"><Plus size={16} /></button>
              </div>
            </div>

            <button onClick={handleAdd}
              className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white font-bebas text-xl tracking-widest py-5 transition-colors mb-3 touch-manipulation">
              <ShoppingCart size={20} />ADD TO ORDER — ${(product.effective_dealer_price * qty).toFixed(2)}
            </button>

            {/* NFA notice */}
            <div className="bg-orange-500/5 border border-orange-500/15 px-3 py-2">
              <p className="font-inter text-[10px] text-orange-300 leading-relaxed">
                NFA Item — ships to your licensed FFL/SOT dealer address. All federal NFA rules apply.
              </p>
            </div>
          </div>
        </div>

        {/* Expanded detail tabs */}
        <ProductDetailTabs product={product} />
      </div>
    </div>
  );
}

function ProductDetailTabs({ product }) {
  const [tab, setTab] = useState('description');
  return (
    <div className="border-t border-white/10">
      <div className="flex border-b border-white/10">
        {[['description','Description'], ['specs','Specifications'], ['features','Features']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`font-inter text-xs px-5 py-3 uppercase tracking-wider transition-colors border-b-2 -mb-px ${tab === key ? 'border-orange-500 text-white' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="p-5">
        {tab === 'description' && (
          <p className="font-inter text-sm text-zinc-400 leading-relaxed">{product.description}</p>
        )}
        {tab === 'specs' && product.specs && (
          <div className="space-y-2">
            {Object.entries(product.specs).map(([k, v]) => (
              <div key={k} className="flex gap-3">
                <span className="font-inter text-xs text-zinc-600 uppercase tracking-wider w-36 flex-shrink-0">{k}</span>
                <span className="font-inter text-sm text-zinc-300">{v}</span>
              </div>
            ))}
          </div>
        )}
        {tab === 'features' && product.features?.length > 0 && (
          <ul className="space-y-2">
            {product.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 font-inter text-sm text-zinc-400">
                <Check size={14} className="text-orange-500 flex-shrink-0 mt-0.5" />{f}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ── Cart Drawer ────────────────────────────────────────────────────────────────
function CartDrawer({ cartItems, cartOpen, onClose, onRemove, onQtyChange, onPlaceOrder, placing, orderError, dealer, paymentRule, token }) {
  const subtotal = cartItems.reduce((s, i) => s + i.product.effective_dealer_price * i.quantity, 0);
  const depositAmount = paymentRule?.rule_type === 'deposit_percent' && paymentRule?.deposit_percent
    ? Math.round(subtotal * paymentRule.deposit_percent / 100 * 100) / 100
    : subtotal;
  const requiresPayment = paymentRule?.rule_type === 'upfront_full' || paymentRule?.rule_type === 'deposit_percent';

  const [showPayForm, setShowPayForm] = useState(false);
  const [card, setCard] = useState({ number: '', expiry: '', cvv: '', name: '' });
  const [charging, setCharging] = useState(false);
  const [chargeError, setChargeError] = useState('');
  const setC = k => e => setCard(p => ({ ...p, [k]: e.target.value }));

  const handlePay = async (orderNum) => {
    setCharging(true); setChargeError('');
    const [mo, yr] = (card.expiry || '/').split('/');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dealer/checkout/pay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          order_number: orderNum, amount: depositAmount,
          card_number: card.number.replace(/\s/g, ''),
          expiry_month: mo?.trim(), expiry_year: yr?.trim(),
          cvv: card.cvv, cardholder_name: card.name || dealer?.business_name || '',
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Card declined');
      setShowPayForm(false);
      setCard({ number: '', expiry: '', cvv: '', name: '' });
    } catch (e) { setChargeError(e.message); }
    setCharging(false);
  };

  if (!cartOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end cursor-pointer bg-black/60" onClick={onClose}>
      <div className="relative bg-[#111] border-l border-white/10 w-full sm:max-w-sm flex flex-col h-full cursor-default"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-white/10 flex-shrink-0">
          <h3 className="font-bebas text-xl text-white tracking-wider">ORDER CART ({cartItems.length})</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white p-2 touch-manipulation"><X size={18} /></button>
        </div>

        {cartItems.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-zinc-600 font-inter text-sm">Cart is empty</div>
        ) : (
          <>
            {/* Scrollable items */}
            <div className="flex-1 divide-y divide-white/5 overflow-y-auto">
              {cartItems.map((item, i) => (
                <div key={i} className="px-4 sm:px-4 py-4">
                  <div className="flex gap-3">
                    <div className="w-14 h-14 bg-[#0f0f0f] flex-shrink-0 overflow-hidden">
                      <img src={imgUrl(item.product.image)} alt="" className="w-full h-full object-contain p-1"
                        onError={e => { e.target.style.display='none'; }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-inter text-sm text-white font-medium leading-tight">{item.product.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <div className="w-3 h-3 rounded-full border border-white/20 flex-shrink-0"
                          style={{ background: FINISH_COLORS[item.finish]?.swatch || '#888' }} />
                        <p className="font-inter text-[10px] text-zinc-500">{FINISH_COLORS[item.finish]?.label || item.finish}</p>
                      </div>
                    </div>
                    <button onClick={() => onRemove(i)} className="text-zinc-700 hover:text-red-400 transition-colors flex-shrink-0 p-2 touch-manipulation"><X size={16} /></button>
                  </div>
                  {/* Qty + price row */}
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-white/10">
                      <button onClick={() => onQtyChange(i, Math.max(1, item.quantity - 1))} className="px-4 py-3 text-zinc-500 hover:text-white touch-manipulation"><Minus size={14} /></button>
                      <span className="px-4 font-inter text-base text-white">{item.quantity}</span>
                      <button onClick={() => onQtyChange(i, item.quantity + 1)} className="px-4 py-3 text-zinc-500 hover:text-white touch-manipulation"><Plus size={14} /></button>
                    </div>
                    <span className="font-bebas text-xl text-white">${(item.product.effective_dealer_price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Sticky bottom: totals + pay */}
            <div className="border-t border-white/10 px-4 sm:px-5 pt-4 pb-6 flex-shrink-0 space-y-3 bg-[#111]">
              <div className="flex items-center justify-between">
                <span className="font-inter text-sm text-zinc-400">Subtotal</span>
                <span className="font-bebas text-2xl text-white">${subtotal.toFixed(2)}</span>
              </div>
              {paymentRule && (
                <div className="flex items-center gap-2 bg-[#1a1a1a] border border-white/10 px-3 py-2">
                  <CreditCard size={12} className="text-orange-400 flex-shrink-0" />
                  <div>
                    <p className="font-inter text-[10px] font-bold text-orange-400 uppercase tracking-wider">{paymentRule.name}</p>
                    {paymentRule.is_default && <p className="font-inter text-[9px] text-zinc-600">Default rule</p>}
                  </div>
                </div>
              )}
              {orderError && <p className="font-inter text-xs text-red-400">{orderError}</p>}

              {!showPayForm ? (
                <button onClick={() => requiresPayment ? setShowPayForm(true) : onPlaceOrder(null, null)}
                  disabled={placing}
                  className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-5 transition-colors touch-manipulation">
                  {requiresPayment ? <><CreditCard size={18} />PAY ${depositAmount.toFixed(2)} & ORDER</> : placing ? 'PLACING...' : 'PLACE ORDER'}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="font-inter text-xs text-zinc-400 flex items-center gap-1"><Lock size={11} />Pay ${depositAmount.toFixed(2)}</p>
                    <button onClick={() => setShowPayForm(false)} className="font-inter text-xs text-zinc-600 hover:text-white p-1 touch-manipulation">Cancel</button>
                  </div>
                  {chargeError && <p className="font-inter text-xs text-red-400">{chargeError}</p>}
                  {/* Card inputs — text-base prevents iOS zoom */}
                  <input type="text" value={card.number} inputMode="numeric"
                    placeholder="4111 1111 1111 1111" maxLength={19}
                    className="w-full bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-mono text-base px-4 py-3.5 outline-none placeholder-zinc-600 touch-manipulation"
                    onChange={e => setCard(p => ({ ...p, number: e.target.value.replace(/[^0-9]/g,'').replace(/(.{4})/g,'$1 ').trim() }))}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={card.expiry} onChange={setC('expiry')} placeholder="MM/YY" maxLength={5} inputMode="numeric"
                      className="bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-mono text-base px-4 py-3.5 outline-none placeholder-zinc-600" />
                    <input value={card.cvv} onChange={setC('cvv')} placeholder="CVV" maxLength={4} inputMode="numeric"
                      className="bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-mono text-base px-4 py-3.5 outline-none placeholder-zinc-600" />
                  </div>
                  <button onClick={async () => { const order = await onPlaceOrder(card, depositAmount); if (order) await handlePay(order.order_number); }}
                    disabled={charging || placing}
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 active:bg-orange-700 disabled:opacity-60 text-white font-bebas text-xl tracking-widest py-5 transition-colors touch-manipulation">
                    <Lock size={16} />{charging ? 'PROCESSING...' : `PAY $${depositAmount.toFixed(2)}`}
                  </button>
                  <p className="font-inter text-[9px] text-zinc-700 text-center">Sandbox: 4111 1111 1111 1111 · 12/30 · 123</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function DealerProducts() {
  const { token, dealer } = useDealerAuth();
  const { setCartCount, cartOpen, setCartOpen } = useDealerCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cartItems, setCartItems] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null);
  const [orderError, setOrderError] = useState('');
  const [paymentRule, setPaymentRule] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);

  // Live pricing: calculate effective dealer price based on cart total quantity
  const totalCartQty = cartItems.reduce((s, i) => s + i.quantity, 0);

  const getEffectivePrice = useCallback((product) => {
    if (!pricingConfig) return product.dealer_price || product.effective_dealer_price || product.price;
    const retail = product.price || 0;
    const isExcluded = (pricingConfig.excluded_skus || []).includes(product.slug);
    const isVolumeActive = totalCartQty >= (pricingConfig.volume_threshold || 5);
    const discountPct = (!isExcluded && isVolumeActive)
      ? (pricingConfig.volume_discount_pct || 50)
      : (pricingConfig.base_discount_pct || 35);
    return Math.round(retail * (1 - discountPct / 100) * 100) / 100;
  }, [pricingConfig, totalCartQty]);

  const isVolumeActive = pricingConfig && totalCartQty >= (pricingConfig.volume_threshold || 5);
  const unitsToVolume = pricingConfig ? Math.max(0, (pricingConfig.volume_threshold || 5) - totalCartQty) : 0;

  useEffect(() => {
    fetch(`${BASE}/api/dealer/products`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(d => setProducts(Array.isArray(d) ? d : []))
      .finally(() => setLoading(false));
    fetch(`${BASE}/api/dealer/payment-rule`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(r => { if (r) setPaymentRule(r); }).catch(() => {});
    fetch(`${BASE}/api/dealer/pricing-config`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(r => { if (r) setPricingConfig(r); }).catch(() => {});
  }, [token]);

  useEffect(() => {
    setCartCount(cartItems.reduce((s, i) => s + i.quantity, 0));
  }, [cartItems, setCartCount]);

  const handleAddToCart = (product, qty, finish) => {
    setCartItems(prev => {
      const existing = prev.findIndex(i => i.product.slug === product.slug && i.finish === finish);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing].quantity += qty;
        return updated;
      }
      return [...prev, { product, quantity: qty, finish }];
    });
    setCartOpen(true);
  };

  const handlePlaceOrder = async (cardData, chargeAmount) => {
    setPlacing(true); setOrderError('');
    try {
      const items = cartItems.map(i => ({
        product_slug: i.product.slug,
        quantity: i.quantity,
        finish: i.finish,
        finish_label: FINISH_COLORS[i.finish]?.label || i.finish,
      }));
      const order = await dealerCreateOrder(token, {
        items, notes: '',
        shipping_address: {
          address: dealer?.business_address || '', city: dealer?.city || '',
          state: dealer?.state || '', zip: dealer?.zip_code || '',
        },
      });
      setCartItems([]);
      setCartOpen(false);
      setOrderSuccess(order);
      setPlacing(false);
      return order;
    } catch (err) {
      setOrderError(err.message);
      setPlacing(false);
      return null;
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (orderSuccess) return (
    <div className="text-center py-16 max-w-md mx-auto">
      <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
        <Check size={32} className="text-orange-500" />
      </div>
      <h2 className="font-bebas text-3xl text-white tracking-wider mb-2">ORDER PLACED</h2>
      <p className="font-inter text-sm text-zinc-400 mb-1">Order <span className="text-orange-400 font-mono">{orderSuccess.order_number}</span> has been received.</p>
      <p className="font-inter text-xs text-zinc-600 mb-6">You'll receive confirmation once it's in production.</p>
      <button onClick={() => setOrderSuccess(null)} className="font-bebas text-base tracking-widest bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 transition-colors">PLACE ANOTHER ORDER</button>
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-bebas text-4xl text-white tracking-wider">SUPPRESSOR CATALOG</h1>
        <p className="font-inter text-sm text-zinc-500 mt-1">
          {products.length} NFA suppressors available · All items require ATF Form 4 transfer
        </p>
      </div>

      {/* Volume pricing banner */}
      {pricingConfig && (
        <div className={`border px-5 py-4 mb-4 transition-all ${isVolumeActive ? 'bg-green-500/10 border-green-500/40' : 'bg-[#1a1a1a] border-white/10'}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 mt-0.5 ${isVolumeActive ? 'text-green-400' : 'text-orange-500'}`}>
                {isVolumeActive ? <Zap size={18} /> : <Tag size={18} />}
              </div>
              <div>
                {isVolumeActive ? (
                  <>
                    <p className="font-bebas text-base text-green-400 tracking-wider">VOLUME PRICING UNLOCKED — {pricingConfig.volume_discount_pct}% OFF ELIGIBLE ITEMS</p>
                    <p className="font-inter text-xs text-zinc-400">Your cart has {totalCartQty} units. Ravener-12 and Mod-1 stay at {pricingConfig.base_discount_pct}% off.</p>
                  </>
                ) : (
                  <>
                    <p className="font-bebas text-base text-white tracking-wider">DEALER PRICING: {pricingConfig.base_discount_pct}% OFF ALL SUPPRESSORS</p>
                    <p className="font-inter text-xs text-zinc-400">
                      {totalCartQty > 0 ? <>Add <strong className="text-orange-400">{unitsToVolume} more unit{unitsToVolume !== 1 ? 's' : ''}</strong> to unlock </> : <>Order <strong className="text-orange-400">{pricingConfig.volume_threshold}+ units</strong> to unlock </>}
                      <strong className="text-green-400">{pricingConfig.volume_discount_pct}% off</strong>{' '}eligible suppressors.
                      {' '}Ravener-12 &amp; Mod-1 always at 35%.
                    </p>
                  </>
                )}
              </div>
            </div>
            {!isVolumeActive && totalCartQty > 0 && (
              <div className="flex-shrink-0 w-32">
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div className="h-full bg-orange-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, (totalCartQty / (pricingConfig.volume_threshold||5)) * 100)}%` }} />
                </div>
                <p className="font-inter text-[9px] text-zinc-500 mt-1 text-right">{totalCartQty} / {pricingConfig.volume_threshold} units</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NFA notice */}
      <div className="bg-orange-500/8 border border-orange-500/20 px-5 py-3 mb-6 flex items-center gap-3">
        <Shield size={16} className="text-orange-500 flex-shrink-0" />
        <p className="font-inter text-xs text-orange-300">
          All items are NFA-regulated suppressors. They ship to your licensed FFL/SOT dealer address.
          ATF Form 4 transfer rules apply to all end-customers.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map(product => {
          const images = product.images?.length ? product.images : [product.image].filter(Boolean);
          const effectivePrice = getEffectivePrice(product);
          const isExcluded = (pricingConfig?.excluded_skus || []).includes(product.slug);
          const discountPct = product.price ? Math.round((1 - effectivePrice / product.price) * 100) : 0;
          return (
            <div key={product.slug}
              className="bg-[#1a1a1a] border border-white/10 hover:border-orange-500/30 transition-all group cursor-pointer"
              onClick={() => setSelectedProduct(product)}
            >
              {/* Product image */}
              <div className="relative aspect-square bg-[#0f0f0f] overflow-hidden">
                <img src={imgUrl(images[0])} alt={product.name}
                  className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-300"
                  onError={e => { e.target.style.display = 'none'; }} />
                {images.length > 1 && (
                  <div className="absolute bottom-2 right-2 font-inter text-[9px] text-zinc-400 bg-black/60 px-1.5 py-0.5">+{images.length - 1} photos</div>
                )}
                <div className="absolute top-2 left-2 flex items-center gap-1 bg-orange-500/90 text-white font-inter text-[9px] font-bold px-2 py-0.5 uppercase tracking-wider">
                  <Shield size={8} />NFA
                </div>
                {isVolumeActive && !isExcluded && (
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-green-600 text-white font-inter text-[9px] font-bold px-2 py-0.5">
                    <Zap size={8} />{discountPct}% OFF
                  </div>
                )}
              </div>

              {/* Product info */}
              <div className="p-4">
                <h3 className="font-bebas text-lg text-white tracking-wider leading-tight mb-0.5 group-hover:text-orange-400 transition-colors">{product.name}</h3>
                <p className="font-inter text-xs text-zinc-500 mb-3 line-clamp-2">{product.short_desc}</p>

                {product.finish_options?.length > 0 && (
                  <div className="flex items-center gap-1.5 mb-3">
                    <span className="font-inter text-[9px] text-zinc-600 uppercase tracking-wider">Finishes:</span>
                    {product.finish_options.map(f => (
                      <div key={f.key} title={f.label || f.key} className="w-4 h-4 rounded-full border border-white/20"
                        style={{ background: FINISH_COLORS[f.key]?.swatch || '#888' }} />
                    ))}
                  </div>
                )}

                {/* Live pricing */}
                <div className="flex items-center justify-between">
                  <div>
                    <div className={`font-bebas text-2xl tracking-wide ${isVolumeActive && !isExcluded ? 'text-green-400' : 'text-orange-400'}`}>
                      ${effectivePrice.toFixed(2)}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-inter text-[10px] text-zinc-600 line-through">${product.price?.toFixed(2)}</span>
                      <span className={`font-inter text-[10px] ${isVolumeActive && !isExcluded ? 'text-green-400' : 'text-orange-400'}`}>
                        {discountPct}% off{isExcluded && isVolumeActive ? ' (locked)' : ''}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 font-inter text-xs text-orange-400 border border-orange-500/30 px-3 py-2 group-hover:bg-orange-500 group-hover:text-white transition-all">
                    View & Order →
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Product detail modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
          onAddToCart={handleAddToCart}
        />
      )}

      {/* Cart drawer */}
      <CartDrawer
        cartItems={cartItems} cartOpen={cartOpen} onClose={() => setCartOpen(false)}
        onRemove={i => setCartItems(prev => prev.filter((_, idx) => idx !== i))}
        onQtyChange={(i, qty) => setCartItems(prev => { const n=[...prev]; n[i]={...n[i],quantity:qty}; return n; })}
        onPlaceOrder={handlePlaceOrder}
        placing={placing} orderError={orderError}
        dealer={dealer} paymentRule={paymentRule} token={token}
      />
    </div>
  );
}
